import { useCallback, useEffect, useState } from "react";
import { MailCheck, Send, Building2, Loader2, CheckCircle2, AlertTriangle, Clock, RefreshCw, ShieldCheck, Server, Copy, Phone, KeyRound, ScrollText, Search, Sparkles, Users } from "lucide-react";
import { toast } from "sonner";
import { DashboardLayout } from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { api, errorMessage, formatDateTime } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const DeliveryBadge = ({ delivery, sentAt }) => {
  if (!delivery && !sentAt) {
    return (
      <Badge variant="secondary" data-testid="delivery-badge-unknown">
        <Clock className="w-3 h-3 mr-1" /> Ingen kode sendt
      </Badge>
    );
  }
  if (!delivery) {
    return (
      <Badge variant="secondary" data-testid="delivery-badge-unknown">
        <Clock className="w-3 h-3 mr-1" /> Sendt (status ukjent)
      </Badge>
    );
  }
  if (delivery.status === "sent") {
    return (
      <Badge className="bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))] hover:bg-[hsl(var(--success))]" data-testid="delivery-badge-sent">
        <CheckCircle2 className="w-3 h-3 mr-1" /> Kode sendt
      </Badge>
    );
  }
  return (
    <Badge className="bg-[hsl(var(--danger))] text-[hsl(var(--danger-foreground))] hover:bg-[hsl(var(--danger))]" data-testid="delivery-badge-failed">
      <AlertTriangle className="w-3 h-3 mr-1" /> Sending feilet
    </Badge>
  );
};

const formatPhone = (p) => (p && /^\d{8}$/.test(p) ? `${p.slice(0, 3)} ${p.slice(3, 5)} ${p.slice(5)}` : p || "");

// Send velkomst-e-posten (kom-i-gang + Vipps-guide) på nytt – til en arrangør som spør, eller en av medarbeiderne
const WelcomeResendCard = () => {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [searching, setSearching] = useState(false);
  const [confirm, setConfirm] = useState(null); // { org, email, name }
  const [sending, setSending] = useState(false);
  const [lastSent, setLastSent] = useState(null);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setItems([]);
      return undefined;
    }
    let alive = true;
    setSearching(true);
    const t = setTimeout(() => {
      api
        .get("/admin/organizers/welcome-search", { params: { q: term } })
        .then((res) => alive && setItems(res.data.items || []))
        .catch((err) => alive && toast.error(errorMessage(err, "Søket feilet")))
        .finally(() => alive && setSearching(false));
    }, 300);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [q]);

  const send = async () => {
    if (!confirm) return;
    setSending(true);
    try {
      const res = await api.post(`/admin/organizers/${confirm.org.id}/send-welcome`, { email: confirm.email });
      toast.success(`Velkomst-e-post sendt til ${res.data.to}`);
      setLastSent({ ...res.data, org_name: confirm.org.org_name });
      setItems((prev) =>
        prev.map((o) =>
          o.id !== confirm.org.id
            ? o
            : confirm.email === o.email
              ? { ...o, welcome_sent_at: res.data.sent_at }
              : { ...o, members: (o.members || []).map((m) => (m.email === confirm.email ? { ...m, welcome_sent_at: res.data.sent_at } : m)) }
        )
      );
      setConfirm(null);
    } catch (err) {
      toast.error(errorMessage(err, "Kunne ikke sende velkomst-e-posten"));
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="border-border" data-testid="admin-welcome-resend-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-heading flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" /> Send velkomst-e-post på nytt
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Kom-i-gang-guiden med Vipps-oppsett som nye arrangører får automatisk. Søk opp arrangøren og send den igjen – til arrangøren selv eller en medarbeider. Sendes på mottakerens målform.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Søk på organisasjon, e-post eller org.nr …"
            className="pl-9"
            aria-label="Søk etter arrangør"
            data-testid="admin-welcome-search-input"
          />
          {searching && <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-muted-foreground" />}
        </div>
        {q.trim().length >= 2 && !searching && items.length === 0 && (
          <p className="text-sm text-muted-foreground" data-testid="admin-welcome-search-empty">Ingen arrangører matcher «{q.trim()}».</p>
        )}
        {items.length > 0 && (
          <ul className="divide-y divide-border rounded-xl border border-border" data-testid="admin-welcome-search-results">
            {items.map((org) => (
              <li key={org.id} className="p-3 space-y-2" data-testid={`admin-welcome-org-${org.id}`}>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium flex items-center gap-2 flex-wrap">
                      <Building2 className="w-4 h-4 shrink-0 text-muted-foreground" />
                      <span className="truncate">{org.org_name}</span>
                      <Badge variant="secondary">Org.nr {org.org_number}</Badge>
                      {!org.email_verified && <Badge variant="outline" className="text-muted-foreground">E-post ikke bekreftet</Badge>}
                    </div>
                    <div className="text-sm text-muted-foreground truncate">
                      {org.full_name ? `${org.full_name} · ` : ""}{org.email}
                      {org.welcome_sent_at ? ` · sist sendt ${formatDateTime(org.welcome_sent_at)}` : " · aldri sendt"}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => setConfirm({ org, email: org.email, name: org.full_name || org.org_name })}
                    data-testid={`admin-welcome-send-${org.id}`}
                  >
                    <Send className="w-4 h-4 mr-1" /> Send til arrangøren
                  </Button>
                </div>
                {(org.members || []).length > 0 && (
                  <ul className="pl-6 space-y-1.5">
                    {org.members.map((m) => (
                      <li key={m.email} className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3 text-sm" data-testid={`admin-welcome-member-${org.id}-${m.email}`}>
                        <span className="min-w-0 flex-1 truncate text-muted-foreground flex items-center gap-1.5">
                          <Users className="w-3.5 h-3.5 shrink-0" />{m.name ? `${m.name} · ` : ""}{m.email}
                          {m.welcome_sent_at ? ` · sist sendt ${formatDateTime(m.welcome_sent_at)}` : ""}
                        </span>
                        <Button variant="secondary" size="sm" onClick={() => setConfirm({ org, email: m.email, name: m.name })} data-testid={`admin-welcome-send-member-${org.id}-${m.email}`}>
                          <Send className="w-3.5 h-3.5 mr-1" /> Send til medarbeider
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        )}
        {lastSent && (
          <p className="text-sm flex items-center gap-1.5" data-testid="admin-welcome-last-sent">
            <CheckCircle2 className="w-4 h-4 text-[hsl(var(--success))]" /> Sendt til {lastSent.to} ({lastSent.org_name}, {lastSent.lang === "nn" ? "nynorsk" : "bokmål"}) {formatDateTime(lastSent.sent_at)}
          </p>
        )}
      </CardContent>

      <AlertDialog open={!!confirm} onOpenChange={(open) => !open && !sending && setConfirm(null)}>
        <AlertDialogContent data-testid="admin-welcome-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Send velkomst-e-post på nytt?</AlertDialogTitle>
            <AlertDialogDescription>
              Velkomst-e-posten med kom-i-gang-guide og Vipps-oppsett sendes til <strong>{confirm?.email}</strong> ({confirm?.name}, {confirm?.org.org_name}).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={sending} data-testid="admin-welcome-cancel-button">Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); send(); }} disabled={sending} data-testid="admin-welcome-confirm-button">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ja, send e-posten"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

// Siste e-poster fra plattformen (alle kanaler) – så leveringsfeil oppdages straks, ikke først når en kunde klager
const EmailLogCard = () => {
  const [log, setLog] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/admin/email/log", { params: { limit: 50, ...(filter !== "all" ? { status: filter } : {}) } })
      .then((res) => setLog(res.data))
      .catch((err) => toast.error(errorMessage(err)))
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const items = log?.items || [];
  const counts = log?.counts || {};

  return (
    <Card className="border-border" data-testid="admin-email-log-card">
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <ScrollText className="w-4 h-4 text-muted-foreground" /> E-postlogg – siste sendinger
          </CardTitle>
          <div className="flex items-center gap-2">
            <Tabs value={filter} onValueChange={setFilter}>
              <TabsList className="h-8">
                <TabsTrigger value="all" className="text-xs px-2.5" data-testid="email-log-filter-all">Alle</TabsTrigger>
                <TabsTrigger value="sent" className="text-xs px-2.5" data-testid="email-log-filter-sent">Sendt</TabsTrigger>
                <TabsTrigger value="failed" className="text-xs px-2.5" data-testid="email-log-filter-failed">Feilet</TabsTrigger>
              </TabsList>
            </Tabs>
            <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="email-log-refresh-button">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
          <div className="rounded-lg border border-border bg-muted/40 px-3 py-2">
            <p className="text-xs text-muted-foreground">Sendt siste 24 t</p>
            <p className="font-medium" data-testid="email-log-sent-24h">{counts.sent_24h ?? "–"}</p>
          </div>
          <div className={`rounded-lg border px-3 py-2 ${counts.failed_24h ? "border-[hsl(var(--danger))]/40 bg-[hsl(var(--danger))]/10" : "border-border bg-muted/40"}`}>
            <p className="text-xs text-muted-foreground">Feilet siste 24 t</p>
            <p className="font-medium" data-testid="email-log-failed-24h">{counts.failed_24h ?? "–"}</p>
          </div>
          <div className="rounded-lg border border-border bg-muted/40 px-3 py-2">
            <p className="text-xs text-muted-foreground">I loggen (90 dager)</p>
            <p className="font-medium" data-testid="email-log-total">{counts.total ?? "–"}</p>
          </div>
        </div>

        {log?.last_failed && (
          <div className="rounded-lg border border-[hsl(var(--danger))]/40 bg-[hsl(var(--danger))]/10 px-4 py-3 text-sm" data-testid="email-log-last-failed">
            <p className="font-medium flex items-center gap-1.5">
              <AlertTriangle className="w-4 h-4 text-[hsl(var(--danger))]" /> Siste feil {formatDateTime(log.last_failed.at)}: «{log.last_failed.subject}»
            </p>
            <p className="text-xs font-mono break-all mt-1 text-muted-foreground">{log.last_failed.error}</p>
          </div>
        )}

        {loading && !log ? (
          <Skeleton className="h-32 w-full rounded-lg" />
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center" data-testid="email-log-empty">
            {filter === "failed" ? "Ingen feilede sendinger – alt har gått gjennom." : "Ingen e-poster i loggen ennå. Alt som sendes fra nå av dukker opp her."}
          </p>
        ) : (
          <div className="overflow-x-auto max-w-full">
            <Table data-testid="email-log-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="whitespace-nowrap">Tidspunkt</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Til</TableHead>
                  <TableHead>Emne</TableHead>
                  <TableHead>Kanal</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((it) => (
                  <TableRow key={it.id} data-testid={`email-log-row-${it.id}`}>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{formatDateTime(it.at)}</TableCell>
                    <TableCell className="text-xs"><Badge variant="secondary">{it.kind_label}</Badge></TableCell>
                    <TableCell className="text-xs max-w-[180px] truncate" title={(it.to || []).join(", ")}>{(it.to || []).join(", ")}</TableCell>
                    <TableCell className="text-xs max-w-[240px] truncate" title={it.subject}>{it.subject}</TableCell>
                    <TableCell className="text-xs uppercase text-muted-foreground">{it.provider}</TableCell>
                    <TableCell>
                      {it.status === "sent" ? (
                        <Badge className="bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))] hover:bg-[hsl(var(--success))]" data-testid={`email-log-status-${it.id}`}>
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Sendt · {it.duration_ms} ms
                        </Badge>
                      ) : (
                        <span className="inline-flex flex-col gap-1">
                          <Badge className="bg-[hsl(var(--danger))] text-[hsl(var(--danger-foreground))] hover:bg-[hsl(var(--danger))]" data-testid={`email-log-status-${it.id}`}>
                            <AlertTriangle className="w-3 h-3 mr-1" /> Feilet
                          </Badge>
                          {it.error && <span className="text-[11px] font-mono break-all text-muted-foreground max-w-[260px]">{it.error}</span>}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          «Sendt» betyr godtatt av e-postleverandøren. Om mottakeren faktisk fikk e-posten ser du i leverandørens logg (Brevo → Transactional → Logs).
        </p>
      </CardContent>
    </Card>
  );
};

const ActiveCode = ({ org }) => {
  const [copied, setCopied] = useState(false);
  if (!org.email_code) {
    return (
      <p className="text-xs text-muted-foreground" data-testid={`admin-code-none-${org.id}`}>
        Ingen aktiv kode – trykk «Send kode på nytt» for å lage en.
      </p>
    );
  }
  const expired = org.email_code_expired === true;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(org.email_code);
      setCopied(true);
      toast.success("Koden er kopiert");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Kunne ikke kopiere – merk koden manuelt");
    }
  };
  return (
    <div
      className={`rounded-lg border px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3 ${
        expired ? "border-border bg-muted/40" : "border-[hsl(var(--ring))]/40 bg-accent/50"
      }`}
      data-testid={`admin-code-box-${org.id}`}
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <KeyRound className="w-4 h-4 text-muted-foreground shrink-0" />
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground">Aktiv bekreftelseskode – kan leses opp til kunden på telefon</p>
          <p
            className={`font-mono text-2xl font-semibold tracking-[0.3em] ${expired ? "text-muted-foreground line-through" : "text-foreground"}`}
            data-testid={`admin-code-value-${org.id}`}
          >
            {org.email_code}
          </p>
          <p className="text-xs text-muted-foreground" data-testid={`admin-code-expiry-${org.id}`}>
            {expired ? "Utløpt" : "Gyldig til"} {org.email_code_expires ? formatDateTime(org.email_code_expires) : "–"}
            {expired ? " – send ny kode for å fortsette" : ""}
          </p>
        </div>
      </div>
      <Button size="sm" variant="secondary" onClick={copy} disabled={expired} data-testid={`admin-code-copy-${org.id}`}>
        {copied ? <CheckCircle2 className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
        {copied ? "Kopiert" : "Kopier"}
      </Button>
    </div>
  );
};

export default function AdminEmailPage() {
  const { organizer } = useAuth();
  const [status, setStatus] = useState(null);
  const [unverified, setUnverified] = useState([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [confirmVerify, setConfirmVerify] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.get("/admin/email/status"), api.get("/admin/organizers/unverified-email")])
      .then(([s, u]) => {
        setStatus(s.data);
        setUnverified(u.data);
      })
      .catch((err) => toast.error(errorMessage(err)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const sendTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.post("/admin/email/test", {});
      setTestResult({ ok: true, ...res.data });
      toast.success(`Test-e-post godtatt av ${res.data.provider} – sjekk innboksen til ${res.data.to}`);
    } catch (err) {
      const msg = errorMessage(err);
      setTestResult({ ok: false, error: msg });
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  const resend = async (org) => {
    setWorking(org.id + "resend");
    try {
      const res = await api.post(`/admin/organizers/${org.id}/resend-verification`);
      toast.success(`Ny kode sendt til ${res.data.to || org.email}`);
      load();
    } catch (err) {
      toast.error(errorMessage(err));
      load();
    } finally {
      setWorking(null);
    }
  };

  const verifyManually = async (org) => {
    setWorking(org.id + "verify");
    try {
      await api.post(`/admin/organizers/${org.id}/verify-email`);
      setUnverified((prev) => prev.filter((o) => o.id !== org.id));
      api.get("/admin/email/status").then((s) => setStatus(s.data)).catch(() => {});
      toast.success(`${org.org_name}: e-post bekreftet manuelt – kontoen kan nå publisere`);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setWorking(null);
      setConfirmVerify(null);
    }
  };

  if (organizer && !organizer.is_admin) {
    return (
      <DashboardLayout>
        <p className="text-muted-foreground" data-testid="admin-denied">Denne siden krever plattform-administrator.</p>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-3xl space-y-6" data-testid="admin-email-page">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight flex items-center gap-2">
            <MailCheck className="w-7 h-7" /> E-postbekreftelse
          </h1>
          <p className="text-muted-foreground mt-1">
            Arrangører som ikke har bekreftet e-postadressen sin, kan ikke publisere. Her ser du om koden faktisk ble sendt, kan lese opp den aktive koden på telefon, sende den på nytt eller bekrefte manuelt.
          </p>
        </div>

        <Card className="border-border" data-testid="admin-email-status-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Server className="w-4 h-4 text-muted-foreground" /> E-postoppsett på serveren
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {loading && !status ? (
              <Skeleton className="h-16 w-full rounded-lg" />
            ) : status ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm min-w-0">
                <div className="rounded-lg border border-border bg-muted/40 px-3 py-2 min-w-0">
                  <p className="text-xs text-muted-foreground">Status</p>
                  <p className="font-medium" data-testid="email-status-enabled">
                    {status.enabled ? "Aktiv" : "Ikke aktivert"}
                    {status.provider ? ` · ${status.provider.toUpperCase()}` : ""}
                  </p>
                </div>
                <div className="rounded-lg border border-border bg-muted/40 px-3 py-2 min-w-0">
                  <p className="text-xs text-muted-foreground">Avsender</p>
                  <p className="font-medium truncate" data-testid="email-status-sender">{status.sender}</p>
                </div>
                <div className="rounded-lg border border-border bg-muted/40 px-3 py-2 min-w-0">
                  <p className="text-xs text-muted-foreground">Server</p>
                  <p className="font-medium truncate" data-testid="email-status-host">
                    {status.smtp_host ? `${status.smtp_host}:${status.smtp_port}` : "–"}
                    {status.smtp_username_masked ? ` (${status.smtp_username_masked})` : ""}
                  </p>
                </div>
                <div className="rounded-lg border border-border bg-muted/40 px-3 py-2">
                  <p className="text-xs text-muted-foreground">Ubekreftede kontoer</p>
                  <p className="font-medium" data-testid="email-status-counts">
                    {status.unverified_count}
                    {status.failed_delivery_count ? ` · ${status.failed_delivery_count} med feilet sending` : ""}
                  </p>
                </div>
              </div>
            ) : null}

            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <Button onClick={sendTest} disabled={testing || (status && !status.enabled)} data-testid="admin-send-test-email-button">
                {testing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
                {testing ? "Sender..." : "Send test-e-post til meg"}
              </Button>
              <p className="text-xs text-muted-foreground">
                Sendes til {organizer?.email}. Viser det faktiske svaret fra e-postleverandøren.
              </p>
            </div>

            {testResult && (
              <div
                className={`rounded-lg border px-4 py-3 text-sm ${
                  testResult.ok
                    ? "border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/10"
                    : "border-[hsl(var(--danger))]/40 bg-[hsl(var(--danger))]/10"
                }`}
                data-testid="admin-test-email-result"
              >
                {testResult.ok ? (
                  <>
                    <p className="font-medium flex items-center gap-1.5">
                      <CheckCircle2 className="w-4 h-4 text-[hsl(var(--success))]" /> Godtatt av {testResult.provider} på {testResult.duration_ms} ms
                    </p>
                    <p className="text-muted-foreground mt-1">{testResult.hint}</p>
                  </>
                ) : (
                  <p className="font-medium flex items-start gap-1.5">
                    <AlertTriangle className="w-4 h-4 text-[hsl(var(--danger))] mt-0.5 shrink-0" /> {testResult.error}
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <WelcomeResendCard />

        <EmailLogCard />

        <div className="flex items-center justify-between">
          <h2 className="text-lg font-heading font-semibold">Venter på e-postbekreftelse</h2>
          <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="admin-email-refresh-button">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Oppdater
          </Button>
        </div>

        {loading && unverified.length === 0 ? (
          <Skeleton className="h-24 w-full rounded-xl" />
        ) : unverified.length === 0 ? (
          <Card className="border-border">
            <CardContent className="py-10 text-center text-muted-foreground" data-testid="admin-email-empty-state">
              Alle arrangører har bekreftet e-postadressen sin.
            </CardContent>
          </Card>
        ) : (
          unverified.map((org) => (
            <Card key={org.id} className="border-border" data-testid={`admin-unverified-${org.id}`}>
              <CardContent className="p-5 flex flex-col gap-4">
                <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="font-heading font-semibold flex items-center gap-2 flex-wrap">
                      <Building2 className="w-4 h-4 shrink-0 text-muted-foreground" />
                      <span className="truncate">{org.org_name}</span>
                      <Badge variant="secondary">Org.nr {org.org_number}</Badge>
                      {org.status === "verified" ? (
                        <Badge variant="outline" className="gap-1"><ShieldCheck className="w-3 h-3" /> BRREG-verifisert</Badge>
                      ) : (
                        <Badge variant="outline">Venter godkjenning</Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {org.full_name || "–"} · <span className="text-foreground" data-testid={`admin-unverified-email-${org.id}`}>{org.email}</span>
                    </p>
                    <p className="text-sm text-muted-foreground flex items-center gap-1.5" data-testid={`admin-unverified-phone-${org.id}`}>
                      <Phone className="w-3.5 h-3.5" />
                      {org.phone ? (
                        <a href={`tel:+47${org.phone}`} className="text-foreground underline underline-offset-4 hover:opacity-80">
                          {formatPhone(org.phone)}
                        </a>
                      ) : (
                        <span>Telefon ikke oppgitt</span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">Registrert {formatDateTime(org.created_at)}</p>
                  </div>
                  <div className="flex flex-col items-start sm:items-end gap-1.5 shrink-0">
                    <DeliveryBadge delivery={org.email_delivery} sentAt={org.email_code_sent_at} />
                    {org.email_code_sent_at && (
                      <p className="text-xs text-muted-foreground">Siste kode sendt {formatDateTime(org.email_code_sent_at)}</p>
                    )}
                  </div>
                </div>

                <ActiveCode org={org} />

                {org.email_delivery?.status === "failed" && org.email_delivery?.error && (
                  <p className="text-xs rounded-md border border-[hsl(var(--danger))]/30 bg-[hsl(var(--danger))]/5 px-3 py-2 font-mono break-all" data-testid={`admin-delivery-error-${org.id}`}>
                    {org.email_delivery.error}
                  </p>
                )}

                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => resend(org)}
                    disabled={!!working || (status && !status.enabled)}
                    data-testid={`admin-resend-code-${org.id}`}
                  >
                    {working === org.id + "resend" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" />Send kode på nytt</>}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setConfirmVerify(org)}
                    disabled={!!working}
                    data-testid={`admin-verify-manually-${org.id}`}
                  >
                    {working === org.id + "verify" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><MailCheck className="w-4 h-4 mr-1" />Bekreft manuelt</>}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <AlertDialog open={!!confirmVerify} onOpenChange={(open) => !open && setConfirmVerify(null)}>
        <AlertDialogContent data-testid="admin-verify-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Bekreft e-post manuelt?</AlertDialogTitle>
            <AlertDialogDescription>
              Du bekrefter at <strong>{confirmVerify?.email}</strong> tilhører {confirmVerify?.org_name}. Gjør dette bare når du har verifisert
              adressen på annen måte (f.eks. på telefon). Kontoen får da lov til å publisere arrangementer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-verify-cancel-button">Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={() => confirmVerify && verifyManually(confirmVerify)} data-testid="admin-verify-confirm-button">
              Ja, bekreft e-posten
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
